<template>
  <b-row class="form-renderer-wrapper">
    <template v-for="field in fields">
      <slot :name="field.key">
        <b-col
          v-bind="field.hasOwnProperty('attrs') ? field.attrs : {cols: 12}"
          :class="field.containerClass ? field.containerClass : ''"
        >
          <b-form-group
            v-if="field.key"
            :description="field.hasOwnProperty('description') ? field.description : null"
            :label-for="field.key"
            :valid-feedback="field.hasOwnProperty('validText') ? field.validText : null"
            :invalid-feedback="getInvalidFeedback(field.key)"
            :state="notValidFields !== null ? !notValidFields.hasOwnProperty(field.key) : null"
            :class="[{'required': field.required}, field.formGroupClass ? field.formGroupClass : '']"
            label-class="d-flex align-items-center"
            :label-class="field.labelClass ? field.labelClass : null"
            :content-cols="field.contentCols ? field.contentCols : null"
          >
            <template #label>
              <span>{{field.label ? field.label : null}}</span>
              <span
                v-if="field.required"
                class="ml-25 required-status"
              >*</span>
              <b-alert
                v-if="field.helpText"
                variant="secondary"
                class="mb-0 mt-25"
                show
              >
                <div class="alert-body p-50">
                  <feather-icon
                    icon="InfoIcon"
                    class="mr-25"
                  ></feather-icon>
                  {{ field.helpText }}
                  <a
                    v-if="field.helpLink"
                    target="_blank"
                    :href="field.helpLink"
                    class="ml-1 pl-50"
                  >{{ field.helpLink }}</a>
                </div>
              </b-alert>
            </template>
            <slot
              :name="getInput(field)"
              :item="field"
            >
              <b-icon-plus-circle-fill
                v-if="field.hasOwnProperty('isVisiblePlus') && field.isVisiblePlus === true"
                variant="success"
                class="cursor-pointer plus-icon action-icon"
                @click="fillField(field)"
                title="Добавить"
              ></b-icon-plus-circle-fill>
              <b-icon-pencil-fill
                v-if="field.isVisibleEdit && field.isVisibleEdit === true"
                variant="success"
                class="cursor-pointer edit-icon action-icon"
                @click="editField(field)"
                title="Изменить"
              ></b-icon-pencil-fill>
              <b-icon-x-circle-fill
                v-if="field.isVisibleClear && field.isVisibleClear === true && formData[field.key]"
                variant="secondary"
                class="cursor-pointer clear-icon action-icon"
                @click="clearField(field)"
                title="Очистить поле"
              />
              <template v-if="field.hasOwnProperty('type') && field.type === 'text'">
                <b-form-input
                  :disabled="field.hasOwnProperty('disabled') && field.disabled === true"
                  :class="field.hasOwnProperty('classes') ? field.classes : {}"
                  @input="onChanged($event, field)"
                  :id="field.key"
                  :placeholder="field.hasOwnProperty('placeholder') ? field.placeholder : ''"
                  :value="formData[field.key]"
                  :state="getState(field.key)"
                />
              </template>
              <template v-if="field.hasOwnProperty('type') && field.type === 'address'">
                <b-form-input
                  :disabled="field.hasOwnProperty('disabled') && field.disabled === true"
                  :class="field.hasOwnProperty('classes') ? field.classes : {}"
                  @input="onChanged($event, field)"
                  :id="field.key"
                  :placeholder="field.hasOwnProperty('placeholder') ? field.placeholder : ''"
                  :value="formData[field.key]"
                  :state="getState(field.key)"
                  :name="field.key"
                  @focus="onFocusAddress(field.key)"
                  autocomplete="do-not-autofill"
                ></b-form-input>
              </template>
              <template v-if="field.hasOwnProperty('type') && field.type === 'number'">
                <b-form-input
                  type="number"
                  :disabled="field.hasOwnProperty('disabled') && field.disabled === true"
                  :class="field.hasOwnProperty('classes') ? field.classes : {}"
                  @input="onChanged($event, field)"
                  :id="field.key"
                  :placeholder="field.hasOwnProperty('placeholder') ? field.placeholder : ''"
                  :value="formData[field.key]"
                />
              </template>

              <template v-if="field.hasOwnProperty('type') && field.type === 'positiveNumber'">
                <b-form-input
                  type="number"
                  :disabled="field.hasOwnProperty('disabled') && field.disabled === true"
                  :class="field.hasOwnProperty('classes') ? field.classes : {}"
                  @input="onChanged($event, field)"
                  :id="field.key"
                  :placeholder="field.hasOwnProperty('placeholder') ? field.placeholder : ''"
                  :value="formData[field.key]"
                  :formatter="positiveNumberFormatter"
                  :state="getState(field.key)"
                />
              </template>

              <template v-if="field.hasOwnProperty('type') && field.type === 'ceilPositiveNumber'">
                <b-form-input
                  type="number"
                  :disabled="field.hasOwnProperty('disabled') && field.disabled === true"
                  :class="field.hasOwnProperty('classes') ? field.classes : {}"
                  @input="onChanged($event, field)"
                  :id="field.key"
                  :placeholder="field.hasOwnProperty('placeholder') ? field.placeholder : ''"
                  :value="formData[field.key]"
                  :invalid-feedback="getInvalidFeedback(field.key)"
                  :state="getState(field.key)"
                  :formatter="ceilPositiveNumberFormatter"
                />
              </template>

              <template v-if="field.hasOwnProperty('type') && field.type === 'textarea'">
                <b-form-textarea
                  @input="onChanged($event, field)"
                  :id="field.key"
                  :placeholder="field.hasOwnProperty('placeholder') ? field.placeholder : ''"
                  :disabled="field.hasOwnProperty('disabled') && field.disabled === true"
                  :readonly="field.hasOwnProperty('readonly') && field.readonly === true"
                  :value="formData[field.key]"
                  :rows="field.hasOwnProperty('rows') ? field.rows : 4"
                  :invalid-feedback="getInvalidFeedback(field.key)"
                  :state="getState(field.key)"
                  :max-rows="field.hasOwnProperty('maxRows') ? field.maxRows : null"
                />
              </template>

              <template v-if="field.hasOwnProperty('type') && field.type === 'password'">
                <b-form-input
                  type="password"
                  :class="field.classes ? field.classes : {}"
                  @input="onChanged($event, field)"
                  :id="field.key"
                  :placeholder="field.hasOwnProperty('placeholder') ? field.placeholder : ''"
                  :value="formData[field.key]"
                ></b-form-input>
              </template>

              <template v-if="field.hasOwnProperty('type') && field.type === 'datetime'">
                <datetime
                  class="theme-custom"
                  type="datetime"
                  @input="onChanged($event, field)"
                  :value="formData[field.key]"
                  :input-id="field.key"
                  :placeholder="field.hasOwnProperty('placeholder') ? field.placeholder : ''"
                  input-class="form-control"
                  :max-datetime="field.maxDatetime ? field.maxDatetime : null"
                  :phrases="{
                    ok: 'Выбрать',
                    cancel: 'Отмена'
                  }"
                  :disabled="field.disabled"
                  :invalid-feedback="getInvalidFeedback(field.key)"
                  :minute-step="field.hasOwnProperty('minuteStep') ? field.minuteStep : minuteStepDefault"
                  :state="getState(field.key)"
                  :class="{'is-invalid': notValidFields && notValidFields.hasOwnProperty(field.key)}"
                />
              </template>

              <template v-if="field.hasOwnProperty('type') && field.type === 'datetime-manual-input'">
                <app-flat-pickr
                  :value="formData[field.key] ? formData[field.key] : ''"
                  :input-id="field.key"
                  :placeholder="field.hasOwnProperty('placeholder') ? field.placeholder : ''"
                  :disabled="field.disabled"
                  :invalid-feedback="getInvalidFeedback(field.key)"
                  :state="getState(field.key)"
                  :config="{
                    enableTime: !!field.enableTime,
                    altInput: true,
                    dateFormat: field.dateFormat ? field.dateFormat : 'Y-m-d H:i:ss',
                    altFormat: field.altFormat ? field.altFormat : 'd.m.Y',
                    maxDate: field.maxDate ? field.maxDate : null,
                    minDate: field.minDate ? field.minDate : null,
                    allowInput: true,
                    defaultHour: Number.isInteger(field.defaultHour) ? field.defaultHour : 12,
                    defaultMinute: Number.isInteger(field.defaultMinute) ? field.defaultMinute : 0,
                    minuteIncrement: Number.isInteger(field.minuteIncrement) ? field.minuteIncrement : 5,
                    onKeyDown: field.addDots ? addDots : () => {}
                  }"
                  :class="[
                    {'is-invalid': notValidFields && notValidFields.hasOwnProperty(field.key)},
                    field.classes ? field.classes : {}
                  ]"
                  @change="onChanged($event, field)"
                ></app-flat-pickr>
              </template>

              <template v-if="field.hasOwnProperty('type') && field.type === 'time'">
                <datetime
                  type="time"
                  @input="onChanged($event, field)"
                  :value="formData[field.key]"
                  :input-id="field.key"
                  :placeholder="field.hasOwnProperty('placeholder') ? field.placeholder : ''"
                  input-class="form-control"
                  :minute-step="field.hasOwnProperty('minuteStep') ? field.minuteStep : minuteStepDefault"
                  :phrases="{
                    ok: 'Выбрать',
                    cancel: 'Отмена'
                  }"
                  class="theme-custom"
                  :class="{'is-invalid': notValidFields && notValidFields.hasOwnProperty(field.key)}"
                />
              </template>

              <template v-if="field.hasOwnProperty('type') && field.type === 'date'">
                <datetime
                  type="date"
                  @input="onChanged($event, field)"
                  :value="formData[field.key]"
                  :input-id="field.key"
                  :placeholder="field.hasOwnProperty('placeholder') ? field.placeholder : ''"
                  input-class="form-control"
                  :phrases="{
                    ok: 'Выбрать',
                    cancel: 'Отмена'
                  }"
                  class="theme-custom"
                />
              </template>

              <template
                v-if="field.hasOwnProperty('type')
                  && field.type === 'select'
                  && field.hasOwnProperty('options')"
              >
                <b-form-select
                  :disabled="field.hasOwnProperty('disabled') && field.disabled === true"
                  @input="onChanged($event, field)"
                  :id="field.key"
                  :value="formData[field.key]"
                  :class="[
                    {'is-invalid': notValidFields && notValidFields.hasOwnProperty(field.key)},
                    field.hasOwnProperty('classes') ? field.classes : {}
                  ]"
                >
                  <b-form-select-option v-if="field.hasOwnProperty('placeholder')" :value="null">
                    {{ field.placeholder }}
                  </b-form-select-option>
                  <b-form-select-option
                    :key="index"
                    :value="item[field.hasOwnProperty('selectSetting') ? field.selectSetting.key : 'id']"
                    v-for="(item, index) in field.options"
                  >
                    {{ getSelectOptionLabel(item, field) }}
                  </b-form-select-option>
                </b-form-select>
              </template>

              <template
                v-if="field.hasOwnProperty('type')
                  && field.type === 'v-single-select'
                  && field.hasOwnProperty('options')"
              >
                <v-select
                  :value="formData[field.key]"
                  :options="field.options"
                  :class="field.hasOwnProperty('classes') ? field.classes : null"
                  :input-id="field.key"
                  :placeholder="field.hasOwnProperty('placeholder') ? field.placeholder : null"
                  :multiple="false"
                  :label="field.multiSelectSetting && field.multiSelectSetting.label ? field.multiSelectSetting.label : null"
                  :disabled="field.disabled"
                  @input="onChanged($event, field)"
                  @input.native="onInputNative($event, field)"
                >
                  <template v-slot:selected-option="option">
                    {{ option[field.multiSelectSetting.label] }}{{ field.multiSelectSetting && field.multiSelectSetting.optionLabelPrefix ? field.multiSelectSetting.optionLabelPrefix : '' }}
                  </template>
                  <template v-slot:option="option">
                    {{ option[field.multiSelectSetting.label] }}{{ field.multiSelectSetting && field.multiSelectSetting.optionLabelPrefix ? field.multiSelectSetting.optionLabelPrefix : '' }}
                  </template>
                  <span slot="no-options">
                    {{ field.multiSelectSetting && field.multiSelectSetting.emptyResultText ? field.multiSelectSetting.emptyResultText : 'Совпадений не найдено' }}
                  </span>
                </v-select>
              </template>

              <template
                v-if="field.hasOwnProperty('type')
                  && field.type === 'v-multi-select'
                  && field.hasOwnProperty('options')"
              >
                <v-select
                  :value="formData[field.key]"
                  :options="field.options"
                  :class="field.hasOwnProperty('classes') ? field.classes : null"
                  :input-id="field.key"
                  :placeholder="field.hasOwnProperty('placeholder') ? field.placeholder : null"
                  :multiple="true"
                  @input="onChanged($event, field)"
                  :label="field.multiSelectSetting && field.multiSelectSetting.label ? field.multiSelectSetting.label : null"
                >
                  <span slot="no-options">
                    {{ field.multiSelectSetting && field.multiSelectSetting.emptyResultText ? field.multiSelectSetting.emptyResultText : 'Совпадений не найдено' }}
                  </span>
                </v-select>
              </template>

              <template
                v-if="field.hasOwnProperty('type')
                  && field.type === 'color'"
              >
                <v-swatches
                  @input="onChanged($event, field)"
                  :value="formData[field.key]"
                  swatches="text-advanced"
                  v-model="color"
                  show-fallback
                  fallback-input-type="color"
                  fallback-ok-text="Выбрать"
                  popover-x="right"
                />
              </template>

              <template
                v-if="field.hasOwnProperty('type')
                  && field.type === 'radio'
                  && field.hasOwnProperty('options')"
              >
                <b-form-radio-group
                  @input="onChanged($event, field)"
                  :id="field.key"
                  v-model="formData[field.key]"
                  :options="field.options"
                  :disabled="field.disabled"
                ></b-form-radio-group>
              </template>

              <template
                v-if="field.hasOwnProperty('type')
                  && field.type === 'radio-group'
                  && field.hasOwnProperty('options')"
              >
                <b-form-radio-group
                  @input="onChanged($event, field)"
                  :id="field.key"
                  v-model="formData[field.key]"
                  :disabled="field.disabled"
                >
                  <b-form-radio
                    v-for="option in field.options"
                    :key="option.value"
                    :value="option.value"
                    :disabled="option.disabled"
                  >
                    {{ option.text }}
                    <img
                      v-if="option.image"
                      :src="option.image.src"
                      alt="radio-image"
                      :style="{
                        width: option.image.width,
                        height: option.image.height,
                      }"
                    >
                  </b-form-radio>
                </b-form-radio-group>
              </template>

              <template v-if="field.hasOwnProperty('type') && field.type === 'checkbox'">
                <b-form-checkbox
                  @change="onChanged($event, field)"
                  :id="field.key"
                  :name="field.key"
                  :disabled="field.disabled && field.disabled === true"
                  :checked="formData[field.key]"
                ></b-form-checkbox>
              </template>

              <template v-if="field.hasOwnProperty('type') && field.type === 'checkbox-group'">
                <b-form-checkbox-group
                  :id="field.key"
                  :name="field.key"
                  :disabled="field.disabled && field.disabled === true"
                  :options="field.options"
                  :value-field="field.valueField ? field.valueField : 'value'"
                  :text-field="field.textField ? field.textField : 'text'"
                  :class="[field.hasOwnProperty('classes') ? field.classes : {}]"
                  v-model="formData[field.key]"
                  @change="onChanged($event, field)"
                ></b-form-checkbox-group>
              </template>

              <template v-if="field.hasOwnProperty('type') && field.type === 'checkbox-group-with-actions'">
                <b-form-checkbox-group
                  :id="field.key"
                  :name="field.key"
                  :disabled="field.disabled && field.disabled === true"
                  :class="[field.hasOwnProperty('classes') ? field.classes : {}]"
                  v-model="formData[field.key]"
                >
                  <b-form-checkbox
                    v-for="option in field.options"
                    :key="option[field.valueField ? field.valueField : 'value']"
                    :value="option"
                    @change="onChanged($event, field)"
                  >
                    <div class="d-flex align-items-center">
                      <span>{{ option[field.textField ? field.textField : 'text'] }}</span>
                      <template v-for="btn in field.buttons">
                        <div
                          v-if="showBtn(btn.type, field.key, option.type)"
                          :title="btn.label"
                          class="d-flex align-items-center"
                          @click.stop.prevent="btn.callback(option)"
                        >
                          <feather-icon
                            class="ml-1"
                            :icon="btn.icon"
                          ></feather-icon>
                        </div>
                      </template>
                    </div>
                  </b-form-checkbox>
                </b-form-checkbox-group>
              </template>

              <template v-if="field.hasOwnProperty('type') && field.type === 'switcher'">
                <b-form-checkbox
                  @change="onChanged($event, field)"
                  :id="field.key"
                  :name="field.key"
                  :disabled="field.disabled && field.disabled === true"
                  :checked="formData[field.key]"
                  inline
                  switch
                ></b-form-checkbox>
              </template>

              <template v-if="field.hasOwnProperty('type') && field.type === 'file'">
                <b-form-file
                  :id="field.key"
                  @change="handleFileUpload($event, field)"
                  :accept="field.hasOwnProperty('accept') ? field.accept : '*'"
                  :browse-text="field.browseText ? field.browseText : 'Выбрать'"
                  :placeholder="field.placeholder ? field.placeholder : ''"
                ></b-form-file>
              </template>

              <template v-if="field.type && field.type === 'image'">
                <image-field
                  :field="field"
                  :form-data.sync="formData"
                  v-on:updateFormData="onUpdateFormData"
                />
              </template>

              <template v-if="field.hasOwnProperty('type') && field.type === 'phone'">
                <the-mask
                  :id="field.key"
                  :mask="field.mask ? field.mask.split(',') : ['(##) ###-##-##', '(###) ###-##-##']"
                  class="form-control"
                  @input="onChanged($event, field)"
                  :value="formData[field.key]"
                  :name="field.key"
                  :invalid-feedback="getInvalidFeedback(field.key)"
                  :state="getState(field.key)"
                  :class="{'is-invalid': notValidFields && notValidFields.hasOwnProperty(field.key)}"
                  :placeholder="field.hasOwnProperty('placeholder') ? field.placeholder : ''"
                />
              </template>
              <template v-if="field.type && field.type === 'dynamicFieldSet'">
                <dynamic-field-set
                  :fieldsSetKey="field.key"
                  :fieldsSet="field.fieldsSet"
                  :form-data.sync="formData"
                  :show-plus-button="field.hasOwnProperty('showPlusButton') ? field.showPlusButton : true"
                  :show-remove-button="field.hasOwnProperty('showRemoveButton') ? field.showRemoveButton : true"
                  :show-add-field-set-button-label="field.hasOwnProperty('showAddFieldSetButtonLabel') ? field.showAddFieldSetButtonLabel : false"
                  :add-field-set-button-label="field.hasOwnProperty('addFieldSetButtonLabel') ? field.addFieldSetButtonLabel : null"
                  :can-remove-all-field-sets="field.hasOwnProperty('canRemoveAllFieldSets') ? field.canRemoveAllFieldSets : false"
                  @updateFormData="onUpdateFormData"
                  @remove-field-set="$emit('remove-field-set')"
                ></dynamic-field-set>
              </template>
              <template v-if="field.type && field.type === 'relatedFieldSet'">
                <related-field-set
                  ref="relatedFieldSetRef"
                  :fieldSetKey="field.key"
                  :sourceField="field.sourceField"
                  :relatedField="field.relatedField"
                  :form-data.sync="formData"
                  :not-valid-fields="notValidFields"
                  @source-field-changed="onSourceFieldChanged($event, field)"
                  @related-field-changed="onRelatedFieldChanged($event, field)"
                />
              </template>

              <template v-if="field.type && field.type === 'quillEditor'">
                <quill-editor
                  :ref="field.key + 'Ref'"
                  :id="field.key"
                  :value="formData[field.key]"
                  :disabled="field.hasOwnProperty('disabled') && field.disabled === true"
                  :readonly="field.hasOwnProperty('readonly') && field.readonly === true"
                  :options="field.editorOptions ? field.editorOptions : null"
                  @input="onChanged($event, field)"
                ></quill-editor>
              </template>

              <template v-if="field.type && field.type === 'app-quill-editor'">
                <app-quill-editor
                  :id="field.key"
                  :value="formData[field.key]"
                  :placeholder="field.hasOwnProperty('placeholder') ? field.placeholder : null"
                  @change-data="onChanged($event, field)"
                ></app-quill-editor>
              </template>

              <template v-if="field.type && field.type === 'separator'">
                <hr :class="[field.hasOwnProperty('classes') ? field.classes : {}]">
              </template>

              <template v-if="field.copyButton && formData[field.key]">
                <div
                  title="Копировать"
                  class="copy-button-wrapper position-absolute d-flex align-items-center justify-content-center cursor-pointer"
                  @click="copyValueToBuffer(formData[field.key])"
                >
                  <b-badge
                    pill
                    variant="primary"
                    class="d-flex align-items-center justify-content-center"
                  >
                    <feather-icon
                      icon="LinkIcon"
                      class="copy-button"
                    ></feather-icon>
                  </b-badge>
                </div>
              </template>
            </slot>
          </b-form-group>
        </b-col>
      </slot>
    </template>
  </b-row>
</template>

<script>
import {
  BCol, BRow, BFormGroup, BFormInput, BFormTextarea, BFormSelectOption, BFormSelect, BFormFile, BButton,
  BIconPlusCircleFill, BIconPencilFill, BIconXCircleFill, BFormCheckbox, BFormRadioGroup, BAlert, BBadge,
  BFormCheckboxGroup, BFormRadio
} from 'bootstrap-vue'
import { TheMask } from 'vue-the-mask'
import VSwatches from 'vue-swatches'
import 'vue-swatches/dist/vue-swatches.css'
import vSelect from 'vue-select'
import DynamicFieldSet from '@/views/components/form/DynamicFieldSet.vue'
import RelatedFieldSet from '@/views/components/form/RelatedFieldSet.vue'
import { quillEditor } from 'vue-quill-editor'
import ImageField from '@/views/components/form/ImageField'
import {
  destroyYmapsLibForAddressInputField,
  initAddressInputField,
  initYmapsLibForAddressInputField
} from '@/composables/addressHelper'
import { onBeforeMount, onBeforeUnmount } from 'vue'
import { copyToBuffer } from '@/composables/copy'
import AppQuillEditor from '@/views/components/app-quill-editor/AppQuillEditor.vue'
import AppFlatPickr from '@/views/components/app-flat-pickr/AppFlatPickr.vue'
import useFlatPickr from '@/composables/flatPickr'

export default {
  name: 'FormRendererComponent',
  components: {
    BBadge, ImageField, VSwatches, BCol, BRow, BButton, BFormGroup, BFormInput, BFormTextarea, BFormSelectOption,
    BFormSelect, BFormFile, TheMask, BIconPlusCircleFill, BFormCheckbox, vSelect, DynamicFieldSet, AppFlatPickr,
    RelatedFieldSet, BIconPencilFill, BIconXCircleFill, quillEditor, BFormRadioGroup, BAlert, BFormCheckboxGroup,
    BFormRadio, AppQuillEditor
  },
  emits: ['updateFormData', 'input-native', 'remove-field-set', 'clear-field', 'fill-field', 'edit-field'],
  data() {
    return {
      color: null,
      isVisiblePlus: false,
      minuteStepDefault: 1,
    }
  },
  props: {
    fields: {
      type: Array,
      required: true
    },
    formData: {
      type: Object,
      default: function () {
        return {}
      }
    },
    notValidFields: {
      type: Object,
      default: function () {
        return null
      }
    }
  },
  setup() {
    const onFocusAddress = fieldKey => initAddressInputField(fieldKey)
    const copyValueToBuffer = text => copyToBuffer(text)
    const { addDots } = useFlatPickr()

    onBeforeMount(() => initYmapsLibForAddressInputField())
    onBeforeUnmount(() => destroyYmapsLibForAddressInputField())

    return {
      onFocusAddress,
      copyValueToBuffer,
      addDots
    }
  },
  methods: {
    getInvalidFeedback(fieldName) {
      return this.notValidFields && this.notValidFields.hasOwnProperty(fieldName)
        ? this.notValidFields[fieldName] : null;
    },
    getState(fieldName) {
      return this.notValidFields && this.notValidFields.hasOwnProperty(fieldName)
        ? false : null;
    },
    getInput(field) {
      return `input(${field.key})`
    },
    fillField(field) {
      this.$emit('fill-field', field);
    },
    clearField(field) {
      this.$emit('clear-field', field);
    },
    editField(field) {
      this.$emit('edit-field', field);
    },
    onChanged(event, field) {
      this.formData[field.key] = event || event === 0 ? event : (['switcher'].includes(field.type) ? false : null)
      this.$emit('updateFormData', {'formData': this.formData, 'field': field})
    },
    onInputNative(event, field) {
      this.$emit('input-native', {'event': event, 'field': field})
    },
    handleFileUpload(event, field) {
      this.formData[field.key] = event.target.files.length ? event.target.files[0] : null
      this.$emit('updateFormData', {'formData': this.formData, 'field': field})
    },
    getSelectOptionLabel(item, field) {
      let label = 'name'
      if (field && field.selectSetting && field.selectSetting.value) {
        let labels = field.selectSetting.value.split(',')
        if (labels.length > 0) {
          label = ''
          labels.forEach(key => {
            label += `${item[key]} `
          })
          return label
        }
      }
      return item[label]
    },
    onUpdateFormData(event) {
      this.$emit('updateFormData', event)
    },
    onSourceFieldChanged(event, field) {
      if (!this.formData[field.key]) {
        this.formData[field.key] = {}
      }
      this.formData[field.key]['sourceField'] = event
      this.$emit('updateFormData', {'formData': this.formData, 'field': field})
    },
    onRelatedFieldChanged(event, field) {
      if (!this.formData[field.key]) {
        this.formData[field.key] = {}
      }
      this.formData[field.key]['relatedField'] = event
      this.$emit('updateFormData', {'formData': this.formData, 'field': field})
    },
    showBtn(btnType, fKey, optionType) {
      return this.formData[fKey]
        ? this.formData[fKey].find(i => i.buttons && i.buttons.length && i.type === optionType) : false
    }
  },
  created() {
    this.fields.forEach(f => {
      if (f.type === 'color' && this.formData[f.key] !== null) {
        this.color = this.formData[f.key]
      }
    })
  },
}
</script>

<style lang='scss' scoped>
.form-renderer-wrapper {
  .copy-button-wrapper {
    bottom: 18px;
    right: 18px;
    height: 30px;
    width: 36px;
    background-color: #ffffff;
    border-radius: 0.357rem;
    .badge {
      height: 26px;
      width: 26px;
      .copy-button {
        height: 18px !important;
        width: 18px !important;
      }
    }
  }
}
</style>
